import { Injectable, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CustomValidators } from 'src/app/_helpers/CustomValidators';
import { dateFromString } from 'src/app/_helpers/utils';
import { COUNTRIES } from 'src/app/_services/constants/countries';
import { FamilyMemberFormService } from 'src/app/_services/family-member-form.service';
import { APIResponse } from 'src/app/_services/types/api-response.type';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import {
  BuildingType,
  ConstructionType,
  CoverageType,
  HeatingSystem,
  RoofType,
  SecurityMeasure,
} from 'src/app/enums/building.enums';

import { ProposalService } from '../../_services/proposal.service';
import { User } from '../../_services/types/auth.types';
import { QuestionnaireAnswers } from '../../_services/types/building.types';
import {
  BuildingInsuranceMetatadata,
  CreateProposalResponse,
  Lead,
} from '../../_services/types/proposals.type';
import { CIVIL_RESPONSABILITY_VALUES, DEDUCTIBLE_VALUES } from './constants';

export type BuildingInsuranceInfoForm = {
  birthDate: FormControl<Date>;
  startDate: FormControl<Date>;
  roomCount: FormControl<number>;
  buildingType: FormControl<BuildingType>;
  buildingDescription: FormControl<string>;
  constructionYear: FormControl<number>;
  securityMeasure: FormControl<SecurityMeasure>;
  address: FormControl<string>;
  heatingType: FormControl<HeatingSystem>;
  heatingDescription: FormControl<string>;
  constructionType: FormControl<ConstructionType>;
  roofType: FormControl<RoofType>;
  appartmentComplexInfo: FormGroup<AppartmentComplexInfoForm>;
  insuranceSum: FormControl<number>;
};

export type AppartmentComplexInfoForm = {
  usage: FormControl<string>;
  appartmentCount: FormControl<number>;
  floorCount: FormControl<number>;
  area: FormControl<number>;
};

export type BuildingCoversInfoFrom = {
  prestations: FormControl<CoverageType[]>;
  franchise: FormControl<number>;
  civilResponsability: FormControl<number>;
};

class BuildingFormData {
  covers: FormGroup<BuildingCoversInfoFrom>;

  insuranceInfo: FormGroup<BuildingInsuranceInfoForm>;

  originalLead?: Lead;

  originalLeadMetadata?: BuildingInsuranceMetatadata;

  originalProposalId?: string;

  originalLeadId?: string;

  constructor() {
    this.insuranceInfo = new FormGroup({
      birthDate: new FormControl<Date>(null, [
        Validators.required,
        CustomValidators.adultAgeValidator,
      ]),
      startDate: new FormControl<Date>(null, [Validators.required, CustomValidators.futureDate]),
      roomCount: new FormControl<number>(0, [Validators.required, Validators.min(0.5)]),
      buildingType: new FormControl<BuildingType>(null, [Validators.required]),
      securityMeasure: new FormControl<SecurityMeasure>(null, [Validators.required]),
      address: new FormControl<string>(null, [Validators.required]),
      heatingType: new FormControl<HeatingSystem>(null, [Validators.required]),
      heatingDescription: new FormControl<string>(null, [Validators.required]),
      constructionType: new FormControl<ConstructionType>(null, [Validators.required]),
      roofType: new FormControl<RoofType>(null, [Validators.required]),
      appartmentComplexInfo: new FormGroup({
        usage: new FormControl<string>(null, [Validators.required]),
        appartmentCount: new FormControl<number>(null, [Validators.required]),
        floorCount: new FormControl<number>(null, [Validators.required]),
        area: new FormControl<number>(null, [Validators.required]),
      }),
      insuranceSum: new FormControl<number>(null, [Validators.required]),
      constructionYear: new FormControl<number>(null, [Validators.required]),
      buildingDescription: new FormControl<string>(null),
    });

    this.covers = new FormGroup({
      prestations: new FormControl<CoverageType[]>(null, [Validators.required]),
      franchise: new FormControl<number>(DEDUCTIBLE_VALUES[0], [Validators.required]),
      civilResponsability: new FormControl<number>(CIVIL_RESPONSABILITY_VALUES[0], [
        Validators.required,
      ]),
    });
  }

  fromUser(user: User) {
    this.insuranceInfo.controls.address.setValue(
      `${user.address}, ${user.postCode} ${user.city}, ${COUNTRIES[user.country]?.fr}`,
    );
    this.insuranceInfo.controls.birthDate.setValue(new Date(user.dob));
  }

  fromLead(lead: Lead) {
    const metadata = lead.metadata as BuildingInsuranceMetatadata;
    this.originalLeadMetadata = metadata;
    this.originalLead = lead;
    this.originalLeadId = lead.leadId;
    this.insuranceInfo.controls.birthDate.setValue(dateFromString(metadata.birthDate));
    this.insuranceInfo.controls.roomCount.setValue(metadata.roomCount);
    this.insuranceInfo.controls.buildingType.setValue(metadata.buildingType);
    this.insuranceInfo.controls.buildingDescription.setValue(metadata.buildingTypeDescription);
    this.insuranceInfo.controls.securityMeasure.setValue(metadata.securityMeasures);
    this.insuranceInfo.controls.address.setValue(metadata.buildingAddress);
    this.insuranceInfo.controls.heatingType.setValue(metadata.heatingSystem);
    this.insuranceInfo.controls.heatingDescription.setValue(metadata.heatingSystemDescriptions);
    this.insuranceInfo.controls.constructionType.setValue(metadata.typeOfConstruction);
    this.insuranceInfo.controls.roofType.setValue(metadata.typeOfRoof);
    this.covers.controls.prestations.setValue(metadata.benefits);
    this.covers.controls.civilResponsability.setValue(metadata.sum);
    this.covers.controls.franchise.setValue(metadata.franchise);
    if (metadata.use || metadata.noOfApartment || metadata.noOfTrade || metadata.commercialArea)
      this.insuranceInfo.controls.appartmentComplexInfo.patchValue({
        usage: metadata.use,
        appartmentCount: metadata.noOfApartment,
        floorCount: metadata.noOfTrade,
        area: metadata.commercialArea,
      });
    this.insuranceInfo.controls.constructionYear.setValue(metadata.yearOfConstruction);
    this.covers.controls.franchise.setValue(metadata.sumInsured);
  }

  toMetadata() {
    const insuranceInfo = this.insuranceInfo.value;
    const covers = this.covers.value;
    return {
      benefits: covers.prestations,
      buildingType: insuranceInfo.buildingType,
      buildingTypeDescription: insuranceInfo.buildingDescription,
      securityMeasures: insuranceInfo.securityMeasure,
      heatingSystem: insuranceInfo.heatingType,
      heatingSystemDescriptions: insuranceInfo.heatingDescription,
      yearOfConstruction: insuranceInfo.constructionYear,
      typeOfConstruction: insuranceInfo.constructionType,
      typeOfRoof: insuranceInfo.roofType,
      sumInsured: insuranceInfo.insuranceSum,
      sum: covers.civilResponsability,
      franchise: covers.franchise,
      use: insuranceInfo.appartmentComplexInfo?.usage,
      noOfApartment: insuranceInfo.appartmentComplexInfo?.appartmentCount,
      noOfTrade: insuranceInfo.appartmentComplexInfo?.floorCount,
      commercialArea: insuranceInfo.appartmentComplexInfo?.area,
      roomCount: insuranceInfo.roomCount,
      buildingAddress: insuranceInfo.address,
      birthDate: insuranceInfo.birthDate.toLocaleDateString('fr-FR'),
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export abstract class BaseBuildingService implements OnDestroy {
  stepperObserver: BehaviorSubject<number[]> = new BehaviorSubject([1, 1]);

  buildingFormData: BuildingFormData = new BuildingFormData();

  navigationChangeSubscription: Subscription;

  flowReady = new BehaviorSubject<boolean>(null);

  flowDone = true;

  constructor(
    protected router: Router,
    protected proposalService: ProposalService,
    protected familyMemberService: FamilyMemberFormService,
    protected route: ActivatedRoute,
    protected authService: UserAuthService,
  ) {}

  abstract sendBuildingInsuranceRequest(
    questionnaire: QuestionnaireAnswers,
  ): Observable<APIResponse<CreateProposalResponse>>;

  resetBuildingForm() {
    this.buildingFormData = new BuildingFormData();
    this.flowDone = true;
    this.familyMemberService.reset();
  }

  abstract handleQueryParams(): void;

  protected fillFromUser(): Observable<boolean> {
    const obs = new BehaviorSubject<boolean>(null);
    if (this.authService.isUserLoggedIn()) {
      this.authService
        .getuserByToken({ token: this.authService.getCurrentUser().token })
        .subscribe({
          next: (res) => {
            this.buildingFormData.fromUser(res.data);
            obs.next(true);
          },
        });
    } else {
      obs.next(true);
    }
    return obs;
  }

  ngOnDestroy(): void {
    this.navigationChangeSubscription.unsubscribe();
  }
}
